import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Power, PowerOff, Save } from "lucide-react";
import { adminApi, formatPromoGrant, formatPromoRedeemed, type GetPromoCodeOutput, type PromoCodeRow } from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import { useDirtyGuardedSeed } from "@/hooks/useDirtyGuardedSeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { promoCodeTone } from "@/lib/status-tone";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/promo-codes_/$code")({
  component: PromoCodeDetailPage,
});

interface FormState {
  note: string;
}

function fromDetail(d: GetPromoCodeOutput): FormState {
  return { note: d.code.note ?? "" };
}

function PromoCodeDetailPage() {
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [initial, setInitial] = useState<FormState | null>(null);
  const [actionMessage, setActionMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.promoCodes.detail(code),
    queryFn: async () => (await adminApi.getPromoCode()({ code })).data,
  });

  const isDirty = !!form && !!initial && form.note !== initial.note;

  useDirtyGuardedSeed(detail.data, isDirty, fromDetail, setForm, setInitial);

  function flash(tone: "success" | "danger", text: string) {
    setActionMessage({ tone, text });
    window.setTimeout(() => setActionMessage(null), 5000);
  }

  function refresh() {
    void qc.invalidateQueries({ queryKey: queryKeys.promoCodes.detail(code) });
    void qc.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
  }

  const saveNote = useMutation({
    mutationFn: async (note: string) => (await adminApi.updatePromoCode()({ code, note })).data,
    onSuccess: (_data, note) => {
      flash("success", "Note saved.");
      setInitial({ note });
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setStatus = useMutation({
    mutationFn: async (status: "active" | "disabled") =>
      (await adminApi.updatePromoCode()({ code, status })).data,
    onSuccess: (_data, status) => {
      flash("success", status === "active" ? "Code re-enabled." : "Code disabled.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/promo-codes" })}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Promo codes
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-mono text-xs text-muted-foreground">{code}</span>
      </div>

      {detail.isLoading ? (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : detail.isError ? (
        <Card>
          <CardContent className="p-8 text-sm text-destructive">
            Failed to load promo code: {(detail.error as Error).message}
          </CardContent>
        </Card>
      ) : detail.data && form && initial ? (
        <>
          <PromoCodeHeader row={detail.data.code} />

          {actionMessage ? (
            <div
              className={cn(
                "rounded-md border p-3 text-sm",
                actionMessage.tone === "success"
                  ? "border-success/40 bg-success/5 text-success"
                  : "border-destructive/40 bg-destructive/5 text-destructive",
              )}
            >
              {actionMessage.text}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-2 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Details
                </h2>
                <Row label="Code">{detail.data.code.code}</Row>
                <Row label="Campaign">{detail.data.code.campaign}</Row>
                <Row label="Status">
                  <StatusPill tone={promoCodeTone(detail.data.code.status)}>
                    {detail.data.code.status}
                  </StatusPill>
                </Row>
                <Row label="Grant">
                  {formatPromoGrant(detail.data.code, formatLondon)}
                </Row>
                <Row label="Redeemed">
                  {formatPromoRedeemed(detail.data.code)}
                </Row>
                <Row label="Expires">
                  {detail.data.code.expiresAt ? (
                    <span title={isoUtc(detail.data.code.expiresAt)}>
                      {formatLondon(detail.data.code.expiresAt)}
                    </span>
                  ) : (
                    "—"
                  )}
                </Row>
                <Row label="Requires school URN">
                  {detail.data.code.requiresSchoolUrn ?? "—"}
                </Row>
                <Row label="Created by">{detail.data.code.createdBy}</Row>
                <Row label="Created">
                  <span title={isoUtc(detail.data.code.createdAt)}>
                    {formatLondon(detail.data.code.createdAt)}
                  </span>
                </Row>
                <Row label="Updated">
                  <span title={isoUtc(detail.data.code.updatedAt)}>
                    {formatLondon(detail.data.code.updatedAt)}
                  </span>
                </Row>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Note
                </h2>
                <Input
                  value={form.note}
                  onChange={(e) => setForm({ note: e.target.value })}
                  maxLength={500}
                  placeholder="Internal note"
                />
                <Button
                  size="sm"
                  disabled={!isDirty || saveNote.isPending}
                  onClick={() => saveNote.mutate(form.note)}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saveNote.isPending ? "Saving…" : "Save note"}
                </Button>

                <h2 className="pt-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Actions
                </h2>
                {detail.data.code.status === "active" ? (
                  <Button
                    variant="destructive"
                    disabled={setStatus.isPending}
                    onClick={() => {
                      if (window.confirm("Disable this code? It can no longer be redeemed.")) {
                        setStatus.mutate("disabled");
                      }
                    }}
                  >
                    <PowerOff className="mr-2 h-4 w-4" /> Disable
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate("active")}
                  >
                    <Power className="mr-2 h-4 w-4" /> Enable
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="p-0">
                <div className="border-b p-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Redemptions ({detail.data.code.redemptionCount})
                    {detail.data.redemptions.length < detail.data.code.redemptionCount ? (
                      <span className="ml-2 font-normal normal-case tracking-normal">
                        — showing the newest {detail.data.redemptions.length}
                      </span>
                    ) : null}
                  </h2>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="px-4 py-2 font-medium">User</th>
                      <th className="px-4 py-2 font-medium">Redeemed</th>
                      <th className="px-4 py-2 font-medium">Granted until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.redemptions.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                          Not redeemed yet.
                        </td>
                      </tr>
                    ) : (
                      detail.data.redemptions.map((r) => (
                        <tr key={r.userId} className="border-b">
                          <td className="px-4 py-2">
                            <Link
                              to="/users/$uid"
                              params={{ uid: r.userId }}
                              className="font-mono text-xs text-primary hover:underline"
                            >
                              {r.userId}
                            </Link>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">
                            <span title={isoUtc(r.redeemedAt)}>{formatLondon(r.redeemedAt)}</span>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">
                            <span title={isoUtc(r.grantedUntil)}>
                              {formatLondon(r.grantedUntil)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function PromoCodeHeader({ row }: { row: PromoCodeRow }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="space-y-1">
          <div className="text-xs font-mono text-muted-foreground">{row.code}</div>
          <h1 className="text-xl font-semibold tracking-tight">{row.campaign}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={promoCodeTone(row.status)}>{row.status}</StatusPill>
            {row.note ? <span className="text-sm text-muted-foreground">{row.note}</span> : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right text-xs">
          <span className="text-muted-foreground">Redeemed</span>
          <span className="font-mono">
            {formatPromoRedeemed(row)}
          </span>
          <span className="text-muted-foreground">Created</span>
          <span className="font-mono">{formatLondon(row.createdAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
      <span className="text-right font-mono text-xs">{children}</span>
    </div>
  );
}
